import React, { useEffect, useMemo, useState } from "react";
import { Users, Wallet, Layers, Plus, MoreHorizontal, Trash2, Landmark, FileText, Info } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { PortalShell } from "@/components/portal/PortalShell";
import { CreateClientForm } from "@/components/admin/CreateClientForm";
import { ClientDetail } from "@/components/admin/ClientDetail";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";

export function AdminDashboard({ user }) {
  const { t, lang } = useLanguage();
  const [section, setSection] = useState("overview");
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [clients, setClients] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const load = async () => {
    const [c, tx] = await Promise.all([
      api.get("/admin/clients").then((r) => r.data),
      api.get("/admin/transactions").then((r) => r.data),
    ]);
    setClients(c); setTransactions(tx);
  };
  useEffect(() => { load(); }, []);

  const fmtMoney = (n) => new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-US", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
  const fmtMoneyStr = (a, c) => new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-US", { style: "currency", currency: c }).format(Number(a));
  const fmtDateTime = (iso) => new Date(iso).toLocaleString(lang === "de" ? "de-DE" : "en-US", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" });

  const totalVolume = clients.reduce((s, c) => s + c.totalVolume, 0);
  const totalTx = clients.reduce((s, c) => s + c.txCount, 0);
  const selected = clients.find((c) => c.id === selectedId) ?? null;

  const remove = async (id) => { await api.delete(`/admin/clients/${id}`); load(); };

  const stats = [
    { icon: Users, label: t("admin.clients.count"), value: String(clients.length) },
    { icon: Wallet, label: t("admin.volume.total"), value: fmtMoney(totalVolume) },
    { icon: Layers, label: t("admin.tx.total"), value: String(totalTx) },
  ];

  const notifications = useMemo(() => {
    const meta = { released: { k: "client.step.released", tone: "emerald" }, received: { k: "client.step.received", tone: "emerald" }, sent: { k: "client.step.reported", tone: "amber" }, pending: { k: "client.step.awaiting", tone: "accent" } };
    return [...transactions].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 8).map((tx) => {
      const m = meta[tx.status] ?? meta.pending;
      return { id: String(tx.id), title: `${tx.investorName} · ${t(m.k)}`, description: `${tx.reference} · ${fmtMoneyStr(tx.amount, tx.currency)}`, at: tx.createdAt, tone: m.tone };
    });
  }, [transactions, lang]);

  const title = selected ? selected.name : t(`portal.section.${section}`);
  const subtitle = t(`portal.section.${section}.sub`);

  return (
    <PortalShell userName={user.name} userRole="admin" active={section} notifications={notifications}
      onNavigate={(s) => { setSection(s); setSelectedId(null); setShowForm(false); }} title={title} subtitle={subtitle}>

      {section === "overview" && (selected ? (
        <div className="mx-auto max-w-4xl">
          <ClientDetail client={selected} transactions={transactions.filter((tx) => tx.userId === selected.id)} onBack={() => setSelectedId(null)} onChanged={load} />
        </div>
      ) : (
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="tech-label inline-flex items-center gap-2 text-muted-foreground"><span className="h-px w-6 bg-accent" />{t("admin.badge")}</span>
              <p className="mt-2 text-sm text-muted-foreground">{t("admin.subtitle")}</p>
            </div>
            <Button onClick={() => setShowForm((v) => !v)}><Plus className="h-4 w-4" />{t("admin.newClient")}</Button>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {stats.map((s) => (
              <Card key={s.label} className="premium-card flex items-center gap-4 p-5">
                <div className="gold-ring flex h-11 w-11 items-center justify-center rounded-lg bg-accent/15 text-accent"><s.icon className="h-5 w-5" /></div>
                <div><p className="tech-label text-muted-foreground">{s.label}</p><p className="gold-text mt-0.5 font-serif text-2xl font-semibold">{s.value}</p></div>
              </Card>
            ))}
          </div>

          {showForm && <div className="mt-6"><CreateClientForm onDone={() => { setShowForm(false); load(); }} /></div>}

          <Card className="premium-card mt-8 overflow-hidden p-0">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h2 className="font-medium text-foreground">{t("admin.clients.title")}</h2>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-0.5 text-xs font-medium text-emerald-600">
                <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" /></span>{t("portal.live")}
              </span>
            </div>
            {clients.length === 0 ? <p className="px-5 py-12 text-center text-sm text-muted-foreground">{t("admin.noClients")}</p> : (
              <div className="overflow-x-auto"><Table>
                <TableHeader><TableRow><TableHead>{t("admin.col.client")}</TableHead><TableHead>{t("admin.col.bank")}</TableHead><TableHead className="text-right">{t("admin.col.volume")}</TableHead><TableHead className="text-right">{t("admin.col.tx")}</TableHead><TableHead className="text-right">{t("admin.col.actions")}</TableHead></TableRow></TableHeader>
                <TableBody>
                  {clients.map((c) => (
                    <TableRow key={c.id} className="cursor-pointer" onClick={() => setSelectedId(c.id)}>
                      <TableCell><span className="font-medium text-foreground">{c.name}</span><span className="block text-xs text-muted-foreground">{c.institutionName}</span></TableCell>
                      <TableCell>{c.account ? (<span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground"><Landmark className="h-3.5 w-3.5 text-accent" />{c.account.bankName}<span className="font-mono text-[11px]">· {c.account.iban.slice(0, 8)}…{c.account.iban.slice(-4)}</span></span>) : <span className="text-xs text-muted-foreground">{t("admin.noBank")}</span>}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums text-foreground">{fmtMoney(c.totalVolume)}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{c.txCount}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setSelectedId(c.id)}>{t("admin.manage")}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { if (window.confirm(t("admin.confirmDelete"))) remove(c.id); }} className="text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />{t("admin.deleteClient")}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></div>
            )}
          </Card>
        </div>
      ))}

      {section === "documents" && (
        <div className="mx-auto max-w-4xl"><Card className="premium-card overflow-hidden p-0">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4"><FileText className="h-5 w-5 text-accent" /><h2 className="font-medium text-foreground">{t("admin.allTx.title")}</h2></div>
          {transactions.length === 0 ? <p className="px-5 py-12 text-center text-sm text-muted-foreground">{t("section.empty")}</p> : (
            <ul className="divide-y divide-border">{transactions.map((tx) => (
              <li key={tx.id} className="flex items-center gap-4 px-5 py-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent"><FileText className="h-5 w-5" /></span>
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-foreground">{tx.investorName}</p><p className="truncate font-mono text-xs text-muted-foreground">{tx.reference}</p></div>
                <div className="text-right"><p className="text-sm font-medium tabular-nums text-foreground">{fmtMoneyStr(tx.amount, tx.currency)}</p><p className="text-[11px] text-muted-foreground">{fmtDateTime(tx.createdAt)}</p></div>
              </li>
            ))}</ul>
          )}
        </Card></div>
      )}

      {section !== "overview" && section !== "documents" && (
        <div className="mx-auto max-w-4xl"><Card className="premium-card flex items-center gap-3 px-5 py-6"><Info className="h-5 w-5 shrink-0 text-accent" /><p className="text-sm text-muted-foreground">{t("admin.central.note")}</p></Card></div>
      )}
    </PortalShell>
  );
}
